// Integrated Iframe Communication Hook
// VAN-021: Main hook that combines parent communication and message listening

import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/core/store";
import { useParentCommunication } from "./useParentCommunication";
import { useMessageListener } from "./useMessageListener";
import { initializeApp, setIframeMode, setSource } from "@/core/app/reducer";
import {
  IframeSource,
  AppReadyData,
  ThemeChangeData,
} from "@/types/iframe-messages";

interface IframeCommunicationOptions {
  // Auto-detection options
  autoDetectIframe?: boolean;
  autoSendReady?: boolean;

  // Message handling
  onThemeChange?: (themeData: ThemeChangeData) => void;
  onParentReady?: () => void;

  // Security options
  trustedOrigins?: string[];
  enableLogging?: boolean;

  // App configuration
  defaultSource?: IframeSource;
  capabilities?: string[];
}

/**
 * Main iframe communication hook that provides complete functionality
 * for iframe-parent window communication
 */
export const useIframeCommunication = (
  options: IframeCommunicationOptions = {}
) => {
  const {
    autoDetectIframe = true,
    autoSendReady = true,
    onThemeChange,
    onParentReady,
    trustedOrigins = ["*"],
    enableLogging = true,
    defaultSource = "delivery",
    capabilities = ["ordering", "theming", "closing"],
  } = options;

  const dispatch = useDispatch();
  const appState = useSelector((state: RootState) => state.app);

  // Utility function to detect source from URL
  const getSourceFromURL = useCallback((): IframeSource | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathSegments = window.location.pathname.split("/").filter(Boolean);

    // Check URL parameters first
    const sourceParam = urlParams.get("source") as IframeSource;
    if (sourceParam && ["preorder", "qr", "delivery"].includes(sourceParam)) {
      return sourceParam;
    }

    // Check path segments
    if (pathSegments.includes("preorder")) return "preorder";
    if (pathSegments.includes("qr")) return "qr";
    if (pathSegments.includes("delivery")) return "delivery";

    // Check for QR-specific patterns
    if (urlParams.has("table") || urlParams.has("tableId")) return "qr";

    return null;
  }, []);

  // Utility function to get table ID from URL (for QR mode)
  const getTableIdFromURL = useCallback((): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("table") || urlParams.get("tableId");
  }, []);

  // Get communication hooks
  const parentComm = useParentCommunication();
  const messageListener = useMessageListener({
    onThemeChange,
    onParentReady: useCallback(() => {
      if (enableLogging) {
        console.log("useIframeCommunication: Parent ready received");
      }
      if (onParentReady) {
        onParentReady();
      }
    }, [onParentReady, enableLogging]),
    enableLogging,
    trustedOrigins,
  });

  // Detect iframe mode and initialize
  useEffect(() => {
    if (!autoDetectIframe) return;

    const isInIframe = window.parent !== window;
    const currentSource = getSourceFromURL() || defaultSource;

    if (enableLogging) {
      console.log("useIframeCommunication: Auto-detection -", {
        isInIframe,
        currentSource,
        currentUrl: window.location.href,
      });
    }

    // Initialize app state
    dispatch(
      initializeApp({
        source: currentSource,
        isIframe: isInIframe,
        capabilities,
      })
    );
  }, []);

  // Auto-send ready message when initialized
  useEffect(() => {
    if (!autoSendReady || !appState.initialized || !appState.isIframe) {
      return;
    }

    const readyData: AppReadyData = {
      source: appState.source,
      route: window.location.pathname,
      brandId: appState.brandId || undefined,
      pointId: appState.pointId || undefined,
      capabilities: appState.capabilities,
    };

    // Add table ID for QR mode
    if (appState.source === "qr") {
      const tableId = getTableIdFromURL();
      if (tableId) {
        readyData.tableId = tableId;
      }
    }

    const success = parentComm.sendAppReady(readyData);

    if (enableLogging) {
      console.log("useIframeCommunication: Auto-sent ready message:", {
        success,
        data: readyData,
      });
    }
  }, [
    autoSendReady,
    appState.initialized,
    appState.isIframe,
    parentComm,
    appState,
    enableLogging,
    getTableIdFromURL,
  ]);

  // Update source manually
  const updateSource = useCallback(
    (newSource: IframeSource) => {
      dispatch(setSource(newSource));
      if (enableLogging) {
        console.log("useIframeCommunication: Source updated to:", newSource);
      }
    },
    [dispatch, enableLogging]
  );

  // Force iframe mode (useful for testing)
  const forceIframeMode = useCallback(
    (enabled: boolean) => {
      dispatch(setIframeMode(enabled));
      if (enableLogging) {
        console.log("useIframeCommunication: Iframe mode forced to:", enabled);
      }
    },
    [dispatch, enableLogging]
  );

  // Send custom ready message with specific data
  const sendCustomReady = useCallback(
    (customData: Partial<AppReadyData>) => {
      console.log("sendCustomReady", customData);
      const readyData: AppReadyData = {
        source: appState.source,
        route: window.location.pathname,
        brandId: appState.brandId || undefined,
        pointId: appState.pointId || undefined,
        capabilities: appState.capabilities,
        ...customData,
      };

      return parentComm.sendAppReady(readyData);
    },
    [parentComm, appState]
  );

  // Complete status information
  const status = {
    isInitialized: appState.initialized,
    isIframe: appState.isIframe,
    parentReady: appState.parentReady,
    source: appState.source,
    capabilities: appState.capabilities,
    theme: appState.theme,
    customColors: appState.customColors,
    isListening: messageListener.isListening,
  };

  return {
    // Status
    status,

    // Parent communication methods
    sendMessage: parentComm.sendMessage,
    sendAppReady: parentComm.sendAppReady,
    requestClose: parentComm.requestClose,
    sendThemeChange: parentComm.sendThemeChange,
    sendCancellation: parentComm.sendCancellation,
    sendError: parentComm.sendError,

    // Utilities
    waitForParent: parentComm.waitForParent,
    updateSource,
    forceIframeMode,
    sendCustomReady,
    getSourceFromURL,
    getTableIdFromURL,

    // Message listener controls
    removeListener: messageListener.removeListener,
    isTrustedOrigin: messageListener.isTrustedOrigin,
  };
};
